import { maxLoanFromMonthly } from '@/pages/blueprint-lite/utils/finance';

import { computeCapitalGains } from './pipeline/capitalGains';
import { computeCashflow } from './pipeline/cashflow';
import { computeIncome } from './pipeline/income';
import { assessLenders } from './pipeline/lenders';
import { computeLenderProse } from './pipeline/lenderProse';
import { computeLiabilities } from './pipeline/liabilities';
import { computeLiving } from './pipeline/living';
import { computeLoan } from './pipeline/property';
import { computeRepayments } from './pipeline/repayments';
import { computeRules } from './pipeline/rules';
import { computeStatus } from './pipeline/status';
import { buildWarnings } from './pipeline/warnings';
import type { CalculationSettings } from './config/calculationSettings';
import type { AtoTaxBracket } from './config/taxBrackets';
import type {
  HemBenchmark,
  LenderPolicy,
  PurchaseAssessmentInput,
  PurchaseAssessmentResult,
  StampDutyBracket,
} from './types';

/**
 * Live-data dependencies that must be supplied to run the assessment.
 * There is no static fallback — the result page is expected to fetch
 * these from the API and hard-fail if they're unavailable.
 */
export interface RunPurchaseAssessmentOptions {
  hemBenchmarks: HemBenchmark[];
  taxBrackets: AtoTaxBracket[];
  stampDutyBrackets: Record<string, StampDutyBracket[]>;
  lenderPanel: LenderPolicy[];
  settings: CalculationSettings;
}

export function runPurchaseAssessment(
  input: PurchaseAssessmentInput,
  options: RunPurchaseAssessmentOptions,
): PurchaseAssessmentResult {
  const { settings } = options;
  const isForeign = input.applicant.citizenship === 'foreign_national';
  const hasPurchase = !!input.purchase;

  const income = computeIncome({
    primary: input.primaryIncome,
    coapplicant: input.coapplicantIncome,
    settings,
  });

  const { hemValue, livingMonthly } = computeLiving({
    situation: input.applicant.familySituation,
    bankIncomeAnnual: income.bankIncome,
    declaredMonthly: input.declaredMonthlyLiving,
    hemBenchmarks: options.hemBenchmarks,
  });

  const loan = computeLoan({
    hasPurchase,
    purchasePrice: input.purchase?.purchasePrice ?? 0,
    depositAvailable: input.purchase?.depositAvailable ?? 0,
    state: input.purchase?.propertyState ?? 'NSW',
    isForeign,
    stampDutyBrackets: options.stampDutyBrackets,
    settings,
  });

  const repayments = computeRepayments({
    loanAmount: loan.loanAmount,
    purchasePrice: input.purchase?.purchasePrice ?? 0,
    rentalYieldPct: input.purchase?.rentalYieldPct ?? 0,
    propertyUse: input.purchase?.propertyUse ?? 'investment',
    hasPurchase,
    existingProperties: input.existingProperties,
    settings,
  });

  const liabilities = computeLiabilities({
    creditCardLimitAud: input.liabilities.creditCardLimit,
    hecsBalance: input.liabilities.hecsBalance,
    otherLoans: input.liabilities.otherLoans,
    coapplicantOtherLoans: input.coapplicantLiabilities?.otherLoans ?? [],
    existingProperties: input.existingProperties,
    hasPurchase,
    settings,
  });

  const adjustedMonthlyIncome =
    income.bankIncome / 12 +
    (input.purchase?.propertyUse === 'investment'
      ? repayments.rentalMonthly * settings.rentalIncludeGlobal
      : 0);
  const surplus =
    adjustedMonthlyIncome -
    livingMonthly -
    liabilities.total -
    repayments.indicativeMonthly;

  const maxBorrow = maxLoanFromMonthly(
    Math.max(0, adjustedMonthlyIncome - livingMonthly - liabilities.total),
    settings.indicativeRate + settings.apraBuffer,
    settings.loanTermMonths,
  );

  const lenders = assessLenders({
    citizenship: input.applicant.citizenship,
    currency: input.primaryIncome.currency,
    baseAud: income.baseAud,
    bonusAud: income.bonusAud,
    grossAud: income.grossAud,
    rentalMonthly: repayments.rentalMonthly,
    livingMonthly,
    liabilitiesMonthly: liabilities.total,
    loanAmount: loan.loanAmount,
    lvrPct: loan.lvr,
    propertyUse: input.purchase?.propertyUse ?? 'investment',
    settings,
  }, options.lenderPanel);

  const cashflow = computeCashflow({
    hasPurchase,
    purchasePrice: input.purchase?.purchasePrice ?? 0,
    rentalMonthly: repayments.rentalMonthly,
    repayMonthly: repayments.indicativeMonthly,
    propertyUse: input.purchase?.propertyUse ?? 'investment',
    propertyType: input.purchase?.propertyType ?? 'house',
    grossAud: income.grossAud,
    taxResidency: input.applicant.taxResidency,
    taxBrackets: options.taxBrackets,
    settings,
  });

  const capitalGains = computeCapitalGains({
    hasPurchase,
    purchasePrice: input.purchase?.purchasePrice ?? 0,
    state: input.purchase?.propertyState ?? 'NSW',
    propertyUse: input.purchase?.propertyUse ?? 'investment',
    taxResidency: input.applicant.taxResidency,
    settings,
  });

  const lenderProse = computeLenderProse(lenders.lenders, {
    citizenship: input.applicant.citizenship,
    currency: input.primaryIncome.currency,
  });

  const status = computeStatus(surplus, loan.lvr, settings);

  const warnings = buildWarnings({
    isForeign,
    citizenship: input.applicant.citizenship,
    hasPurchase,
    surplus,
    lvr: loan.lvr,
    depositGap: loan.depositGap,
    totalRequired: loan.totalRequired,
    stampDutyTotal: loan.stampDuty.total,
    fees: loan.fees,
    purchasePrice: input.purchase?.purchasePrice ?? 0,
    effectiveDeposit: loan.effectiveDeposit,
    taxResidency: input.applicant.taxResidency,
  });

  const rules = computeRules({
    citizenship: input.applicant.citizenship,
    currency: input.primaryIncome.currency,
    employment: input.applicant.employment,
    lvr: loan.lvr,
    surplus,
    hasPurchase,
    passCount: lenders.passCount,
    isForeign,
    grossAud: income.grossAud,
    liabilitiesMonthly: liabilities.total,
    depositGap: loan.depositGap,
    bonusAud: income.bonusAud,
    baseAud: income.baseAud,
    cashflow,
    propertyUse: input.purchase?.propertyUse ?? 'investment',
    taxResidency: input.applicant.taxResidency,
  });

  return {
    input,
    income,
    livingMonthly,
    hemValue,
    liabilities,
    loan,
    repayments,
    adjustedMonthlyIncome,
    surplus,
    maxBorrow,
    status,
    lenders: lenders.lenders,
    passCount: lenders.passCount,
    reviewCount: lenders.reviewCount,
    failCount: lenders.failCount,
    borrowingLow: lenders.borrowingLow,
    borrowingHigh: lenders.borrowingHigh,
    warnings,
    cashflow,
    capitalGains,
    rules,
    lenderProse,
  };
}
